/**
 * Public question-rating page for the human evaluation study.
 * Reached via a unique per-rater link: /rate/:token
 * Every answer is saved to the server immediately, so raters can
 * close the tab and resume later from where they left off.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import apiClient from '../../api/client';

interface RatingQuestion {
  sheet_id: string;
  order: number;
  question_text: string;
  options: string;
}

interface RatingAnswer {
  sheet_id: string;
  bloom_level: number;
  correctness: number;
  relevance: number;
  clarity: number;
  depth: number;
}

interface SessionData {
  rater_label: string;
  total: number;
  questions: RatingQuestion[];
  answers: Record<string, RatingAnswer>;
}

const BLOOM_LEVELS = [
  { level: 1, name: 'Remember', hint: 'recall a fact or definition' },
  { level: 2, name: 'Understand', hint: 'explain or interpret a concept' },
  { level: 3, name: 'Apply', hint: 'use a method in a concrete situation' },
  { level: 4, name: 'Analyze', hint: 'break into parts and relate them' },
  { level: 5, name: 'Evaluate', hint: 'judge with criteria, defend a position' },
  { level: 6, name: 'Create', hint: 'produce something new by combining ideas' },
];

const DIMENSIONS: { key: keyof Omit<RatingAnswer, 'sheet_id' | 'bloom_level'>; label: string; hint: string }[] = [
  { key: 'correctness', label: 'Correctness', hint: 'factually and technically sound' },
  { key: 'relevance', label: 'Relevance', hint: 'reasonable for this course' },
  { key: 'clarity', label: 'Clarity', hint: 'unambiguous and well phrased' },
  { key: 'depth', label: 'Depth', hint: 'demands real thought at its level' },
];

type Draft = Partial<Omit<RatingAnswer, 'sheet_id'>>;

const RatingPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [session, setSession] = useState<SessionData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [index, setIndex] = useState(0);
  const [draft, setDraft] = useState<Draft>({});
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    if (!token) return;
    apiClient
      .get<SessionData>(`/rating/${token}`)
      .then((res) => {
        setSession(res.data);
        const firstUnanswered = res.data.questions.findIndex(
          (q) => !res.data.answers[q.sheet_id]
        );
        setIndex(firstUnanswered === -1 ? 0 : firstUnanswered);
      })
      .catch(() => setError('This rating link is not valid. Please check the URL you received.'));
  }, [token]);

  const question = session?.questions[index];

  useEffect(() => {
    if (!session || !question) return;
    const existing = session.answers[question.sheet_id];
    setDraft(existing ? { ...existing } : {});
  }, [session, question]);

  const answeredCount = useMemo(
    () => (session ? Object.keys(session.answers).length : 0),
    [session]
  );

  const complete =
    draft.bloom_level !== undefined &&
    DIMENSIONS.every((d) => draft[d.key] !== undefined);

  const save = async (advance: boolean) => {
    if (!session || !question || !complete || !token) return;
    setSaving(true);
    try {
      const payload = { sheet_id: question.sheet_id, ...draft } as RatingAnswer;
      await apiClient.post(`/rating/${token}/answers`, payload);
      setSession({
        ...session,
        answers: { ...session.answers, [question.sheet_id]: payload },
      });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1200);
      if (advance && index < session.total - 1) setIndex(index + 1);
    } catch {
      setError('Saving failed. Check your connection and try again; your other answers are safe.');
    } finally {
      setSaving(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="max-w-md text-center text-gray-700 dark:text-gray-200">{error}</div>
      </div>
    );
  }
  if (!session || !question) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        Loading…
      </div>
    );
  }

  const options = (question.options || '')
    .split(' | ')
    .map((o) => o.trim())
    .filter(Boolean);

  const pill = (active: boolean) =>
    `px-3 py-1.5 rounded-full text-sm border transition ${
      active
        ? 'bg-blue-600 text-white border-blue-600'
        : 'bg-white dark:bg-secondary-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-secondary-600 hover:border-blue-400'
    }`;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-secondary-900 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-4">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
            Question Rating Study
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {session.rater_label} · {answeredCount} of {session.total} completed ·
            every answer saves instantly, you can leave and resume anytime
          </p>
          <div className="mt-2 h-2 bg-gray-200 dark:bg-secondary-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-600 transition-all"
              style={{ width: `${(answeredCount / session.total) * 100}%` }}
            />
          </div>
        </div>

        {/* Instructions */}
        <details
          className="mb-4 bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-gray-200 dark:border-secondary-700"
          open={answeredCount === 0}
        >
          <summary className="cursor-pointer select-none px-6 py-3 font-medium text-gray-900 dark:text-white">
            Instructions (please read before starting)
          </summary>
          <div className="px-6 pb-5 text-sm text-gray-700 dark:text-gray-300 space-y-3">
            <p>
              You will rate quiz questions from an undergraduate software
              engineering course. For each question, first classify the
              cognitive level it demands using Bloom's taxonomy, then rate its
              quality on four dimensions. Work independently: please do not
              discuss your ratings with the other rater until both of you have
              finished all questions.
            </p>
            <div>
              <div className="font-medium text-gray-900 dark:text-white mb-1">
                Bloom's taxonomy levels
              </div>
              <ul className="space-y-0.5">
                {BLOOM_LEVELS.map((b) => (
                  <li key={b.level}>
                    <span className="font-medium">L{b.level} {b.name}:</span>{' '}
                    the student must {b.hint}.
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-gray-500 dark:text-gray-400">
                Judge what the question actually requires, not the verbs it
                uses. A question saying "design" that only needs a recalled
                definition is L1.
              </p>
            </div>
            <div>
              <div className="font-medium text-gray-900 dark:text-white mb-1">
                Quality dimensions (1 = very poor, 5 = excellent)
              </div>
              <ul className="space-y-0.5">
                {DIMENSIONS.map((d) => (
                  <li key={d.key}>
                    <span className="font-medium">{d.label}:</span> {d.hint}.
                  </li>
                ))}
              </ul>
            </div>
            <p className="text-gray-500 dark:text-gray-400">
              Every answer is saved the moment you press Save, so you can close
              this page at any point and continue later from the same link.
              There are no right answers on quality; your honest expert
              judgment is the measurement.
            </p>
          </div>
        </details>

        {/* Question navigator */}
        <div className="mb-4 flex flex-wrap gap-1">
          {session.questions.map((q, i) => {
            const done = !!session.answers[q.sheet_id];
            const current = i === index;
            return (
              <button
                key={q.sheet_id}
                onClick={() => setIndex(i)}
                title={`Question ${i + 1}`}
                className={`w-6 h-6 rounded text-[10px] border ${
                  current
                    ? 'border-blue-600 ring-2 ring-blue-300 '
                    : 'border-transparent '
                }${
                  done
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 dark:bg-secondary-700 text-gray-600 dark:text-gray-300'
                }`}
              >
                {i + 1}
              </button>
            );
          })}
        </div>

        {/* Question card */}
        <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-gray-200 dark:border-secondary-700 p-6 mb-4">
          <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">
            Question {index + 1} of {session.total}
          </div>
          <p className="text-gray-900 dark:text-white leading-relaxed whitespace-pre-wrap">
            {question.question_text}
          </p>
          {options.length > 0 && (
            <ul className="mt-3 space-y-1 text-sm text-gray-600 dark:text-gray-300">
              {options.map((o) => (
                <li key={o} className="pl-2">{o}</li>
              ))}
            </ul>
          )}
        </div>

        {/* Bloom level */}
        <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-gray-200 dark:border-secondary-700 p-6 mb-4">
          <div className="font-medium text-gray-900 dark:text-white mb-1">
            1. Which cognitive level does this question demand?
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            Judge what it actually requires, not the verbs it uses.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {BLOOM_LEVELS.map((b) => (
              <button
                key={b.level}
                onClick={() => setDraft({ ...draft, bloom_level: b.level })}
                className={`text-left rounded-lg border p-2.5 transition ${
                  draft.bloom_level === b.level
                    ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/30'
                    : 'border-gray-200 dark:border-secondary-600 hover:border-blue-300'
                }`}
              >
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  L{b.level} {b.name}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{b.hint}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Quality dimensions */}
        <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-gray-200 dark:border-secondary-700 p-6 mb-4">
          <div className="font-medium text-gray-900 dark:text-white mb-3">
            2. Rate the question quality (1 = very poor, 5 = excellent)
          </div>
          <div className="space-y-4">
            {DIMENSIONS.map((d) => (
              <div key={d.key} className="flex flex-wrap items-center gap-3">
                <div className="w-32">
                  <div className="text-sm text-gray-900 dark:text-white">{d.label}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{d.hint}</div>
                </div>
                <div className="flex gap-1.5">
                  {[1, 2, 3, 4, 5].map((v) => (
                    <button
                      key={v}
                      onClick={() => setDraft({ ...draft, [d.key]: v })}
                      className={pill(draft[d.key] === v)}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIndex(Math.max(0, index - 1))}
            disabled={index === 0}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-secondary-600 text-gray-700 dark:text-gray-200 disabled:opacity-40"
          >
            Back
          </button>
          <button
            onClick={() => save(true)}
            disabled={!complete || saving}
            className="px-5 py-2 rounded-lg bg-blue-600 text-white font-medium disabled:opacity-40 hover:bg-blue-700 transition"
          >
            {saving ? 'Saving…' : index === session.total - 1 ? 'Save' : 'Save and next'}
          </button>
          {savedFlash && <span className="text-sm text-green-600">Saved</span>}
          {!complete && (
            <span className="text-sm text-gray-400">
              select a level and all four ratings to save
            </span>
          )}
        </div>

        {answeredCount === session.total && (
          <div className="mt-6 p-4 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 text-sm">
            All {session.total} questions completed. Thank you! You can still
            revisit any question above and change your answer.
          </div>
        )}
      </div>
    </div>
  );
};

export default RatingPage;
